import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Checkbox,
  Radio,
  Text,
  TextFieldSize,
  ToastAnimateFrom,
  ToastContent,
  ToastPosition,
  ToastType,
} from '@bechtel/gds';
import {
  CableTermType,
  CommodityName,
  ConstructionStatus,
  MetadataTableName,
  PrimaryStatus,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { useFormikContext } from 'formik';
import { filter, find, get, includes, isEmpty, lowerCase, map, omit, toUpper } from 'lodash';
import { useEffect, useMemo } from 'react';
import {
  CommodityFooter,
  Filter,
  FormField,
  FormLabelSize,
  MultiLingualText,
  SelectFieldComponent,
  useLanguage,
} from '~/components';
import MessageModal from '~/components/organisms/message-modal/message';
import { usePermissions, useSelectListQuery, useToast } from '~/hooks';
import { OverridePrimaryStatusFormikState } from '../types';
import { ContentStyled, FormContainerStyled, FormStyled, ListSelectionStyled, SectionHeaderStyled } from './styled';
import { customMetaData } from './utils';

const buildGqlMutation = (entityName: string) => {
  const rawMutation = `mutation Override${entityName}PrimaryStatus($override${entityName}PrimaryStatusInput: Override${entityName}PrimaryStatusInput!) {
    override${entityName}PrimaryStatus(override${entityName}PrimaryStatusInput: $override${entityName}PrimaryStatusInput) {
      errors
      ids
    }
  }`;

  return gql`
    ${rawMutation}
  `;
};

const SelectedItemComponent = (items) => {
  return <Text>{items.item.description}</Text>;
};

const ItemComponent = (items) => {
  return <Text>{items.item.description}</Text>;
};

const OverridePrimaryStatusForm = () => {
  const { showToast } = useToast();
  const { getMultiLingualTextByKey } = useLanguage();
  const entityText = getMultiLingualTextByKey('RECORDS');
  const [getSelectListData, { data }] = useSelectListQuery('CABLE.CONSTRUCTION_STATUS');
  const { values, dirty, touched, handleReset, setFieldValue } = useFormikContext<OverridePrimaryStatusFormikState>();
  const { isSystemAdmin, canOverridePriStatAll, canOverridePriStatJtoR } = usePermissions();
  const canUpdatePrimaryStatus = isSystemAdmin || canOverridePriStatAll;

  const getPrimaryStatusValues = (name) => {
    const listData = [
      {
        id: PrimaryStatus.I,
        description: `${PrimaryStatus.I} - ${getMultiLingualTextByKey('INFERRED')}`,
        value: PrimaryStatus.I,
      },
      {
        id: PrimaryStatus.P,
        description: `${PrimaryStatus.P} - ${getMultiLingualTextByKey('PRELIMINARY')}`,
        value: PrimaryStatus.P,
      },
      {
        id: PrimaryStatus.R,
        description: `${PrimaryStatus.R} - ${getMultiLingualTextByKey('RELEASED_BY_ENGINEERING')}`,
        value: PrimaryStatus.R,
      },
      {
        id: PrimaryStatus.J,
        description: `${PrimaryStatus.J} - ${getMultiLingualTextByKey('JOBSITE_RECEIVED')}`,
        value: PrimaryStatus.J,
      },
      {
        id: PrimaryStatus.C,
        description: `${PrimaryStatus.C} - ${getMultiLingualTextByKey('CONSTRUCTION_COMPLETE')}`,
        value: PrimaryStatus.C,
      },
    ];
    // for oldPrimaryStatus return all values
    if (name === 'oldPrimaryStatus') {
      const primaryStatusValues = canOverridePriStatJtoR
        ? filter(listData, (item) => item.id === PrimaryStatus.J)
        : listData;
      if (values.commodityType != CommodityName.EQUIPMENT) {
        return filter(primaryStatusValues, (item) => item.id != PrimaryStatus.I);
      }
      return primaryStatusValues;
    }
    // for newPrimaryStatus return  only ['R','J','C']
    else {
      const primaryStatusValues = canOverridePriStatJtoR
        ? filter(listData, (item) => item.id === PrimaryStatus.R)
        : listData;
      return filter(primaryStatusValues, (item) => !includes([PrimaryStatus.I, PrimaryStatus.P], item.id));
    }
  };

  const constructionStatusValues = useMemo(
    () =>
      map(data?.getSelectListData?.items, (item) => {
        return { id: item.column1, description: item.column2, value: item.column1 };
      }),
    [data]
  );
  const entityName = values.commodityType === MetadataTableName.CABLE_TERM ? 'CableTerm' : values.commodityType;
  const query = buildGqlMutation(entityName);

  const selectedItem = (data, value) => find(data, (selectListItem) => selectListItem.value === value);

  const [overridePrimaryStatus, { data: overridePrimaryStatusData, loading }] = useMutation(query, {
    onCompleted: (data) => {
      if (data[`override${entityName}PrimaryStatus`].ids != null) {
        showToast(
          UiTextEnum.SAVED_SUCCESSFULLY_TOAST,
          [getMultiLingualTextByKey(toUpper(entityName)), entityText],
          ToastType.INFO
        );
      }
    },
    update(cache) {
      cache.evict({ fieldName: `search${entityName}s` });
      cache.evict({ fieldName: `${lowerCase(entityName)}Count` });
      cache.gc();
    },
    onError: () => {
      showToast(
        UiTextEnum.ERROR_WHILE_UPDATING_TOAST,
        [getMultiLingualTextByKey(toUpper(values.commodityType)), entityText],
        ToastType.WARNING
      );
    },
  });

  useEffect(() => {
    handleReset();
    if (values.commodityType === CommodityName.EQUIPMENT && !canOverridePriStatJtoR)
      setFieldValue('oldPrimaryStatus', PrimaryStatus.I);
    setFieldValue('commodityType', values.commodityType);
  }, [values.commodityType, handleReset, setFieldValue, canOverridePriStatJtoR]);

  useEffect(() => {
    setFieldValue('retainReels', values.retainReels);
  }, [values.commodityType, values.retainReels, setFieldValue]);

  useEffect(() => {
    getSelectListData();
  }, [getSelectListData]);

  const getSanitizedValues = () => {
    if (values.commodityType === CommodityName.CABLE) {
      return { ...omit(values, ['cableTermType', 'commodityType']) };
    } else if (values.commodityType === MetadataTableName.CABLE_TERM) {
      return { ...omit(values, ['retainReels', 'commodityType']) };
    } else {
      return { ...omit(values, ['cableTermType', 'retainReels', 'commodityType']) };
    }
  };

  const saveHandler = async () => {
    const sanitizedValues = getSanitizedValues();
    await overridePrimaryStatus({
      variables: {
        [`override${entityName}PrimaryStatusInput`]: {
          ...sanitizedValues,
          whereClause: JSON.parse(sanitizedValues?.whereClause),
        },
      },
    });
  };

  const errorKeys: UiMessageKey[] = get(overridePrimaryStatusData, `override${entityName}PrimaryStatus.errors`) ?? [];

  const newConstructionStatus = getMultiLingualTextByKey('NEW_CONSTRUCTION_STATUS');
  const oldPrimaryStatus = getMultiLingualTextByKey('CURRENT_PRIMARY_STATUS');
  const newPrimaryStatus = getMultiLingualTextByKey('NEW_PRIMARY_STATUS');

  const newConstructionStatusMetadata = {
    ...customMetaData,
    memberName: 'newConstructionStatus',
    description: 'New Construction Status',
    alternateDescription: newConstructionStatus,
    columnPrompt: 'New Construction Status',
    alternateColumnPrompt: newConstructionStatus,
  };

  const oldPrimaryStatusMetadata = {
    ...customMetaData,
    memberName: 'oldPrimaryStatus',
    description: 'Current Primary Status',
    alternateDescription: oldPrimaryStatus,
    columnPrompt: 'Current Primary Status',
    alternateColumnPrompt: oldPrimaryStatus,
  };

  const newPrimaryStatusMetadata = {
    ...customMetaData,
    memberName: 'newPrimaryStatus',
    description: 'New Primary Status',
    alternateDescription: newPrimaryStatus,
    columnPrompt: 'New Primary Status',
    alternateColumnPrompt: newPrimaryStatus,
  };

  return (
    <Box flexGrow={{ sm: ' 1' }} flexDirection={{ sm: 'column' }}>
      <FormStyled>
        <FormContainerStyled>
          <ContentStyled>
            <Box flexDirection={{ sm: 'column' }}>
              <SectionHeaderStyled>
                <MultiLingualText size="400" weight="600" color="grey.800">
                  Component Type
                </MultiLingualText>
              </SectionHeaderStyled>
              <Box flexDirection={{ sm: 'column' }}>
                <ListSelectionStyled
                  disabled={!canUpdatePrimaryStatus}
                  onClick={() => {
                    if (!canUpdatePrimaryStatus) return;
                    setFieldValue('commodityType', CommodityName.CABLE);
                  }}
                >
                  <Box mr={{ sm: '500' }}>
                    <Radio
                      size={TextFieldSize.MEDIUM}
                      checked={values.commodityType === CommodityName.CABLE}
                      readOnly
                    />
                  </Box>
                  <MultiLingualText size="300" color="grey.900">
                    Cable
                  </MultiLingualText>
                </ListSelectionStyled>
                <ListSelectionStyled
                  disabled={!canUpdatePrimaryStatus}
                  onClick={() => {
                    if (!canUpdatePrimaryStatus) return;
                    setFieldValue('commodityType', CommodityName.EQUIPMENT);
                  }}
                >
                  <Box mr={{ sm: '500' }}>
                    <Radio
                      size={TextFieldSize.MEDIUM}
                      checked={values.commodityType === CommodityName.EQUIPMENT}
                      readOnly
                    />
                  </Box>
                  <MultiLingualText size="300" color="grey.900">
                    Equipment
                  </MultiLingualText>
                </ListSelectionStyled>
                <ListSelectionStyled
                  disabled={!canUpdatePrimaryStatus}
                  onClick={() => {
                    if (!canUpdatePrimaryStatus) return;
                    setFieldValue('commodityType', CommodityName.JUMPER);
                  }}
                >
                  <Box mr={{ sm: '500' }}>
                    <Radio
                      size={TextFieldSize.MEDIUM}
                      checked={values.commodityType === CommodityName.JUMPER}
                      readOnly
                    />
                  </Box>
                  <MultiLingualText size="300" color="grey.900">
                    Jumper
                  </MultiLingualText>
                </ListSelectionStyled>
                <ListSelectionStyled
                  disabled={!canUpdatePrimaryStatus}
                  onClick={() => {
                    if (!canUpdatePrimaryStatus) return;
                    setFieldValue('commodityType', CommodityName.RACEWAY);
                  }}
                >
                  <Box mr={{ sm: '500' }}>
                    <Radio
                      size={TextFieldSize.MEDIUM}
                      checked={values.commodityType === CommodityName.RACEWAY}
                      readOnly
                    />
                  </Box>
                  <MultiLingualText size="300" color="grey.900">
                    Raceway
                  </MultiLingualText>
                </ListSelectionStyled>
                <ListSelectionStyled
                  disabled={!canUpdatePrimaryStatus}
                  onClick={() => {
                    if (!canUpdatePrimaryStatus) return;
                    setFieldValue('commodityType', MetadataTableName.CABLE_TERM);
                  }}
                >
                  <Box mr={{ sm: '500' }}>
                    <Radio
                      size={TextFieldSize.MEDIUM}
                      checked={values.commodityType === MetadataTableName.CABLE_TERM}
                      readOnly
                    />
                  </Box>
                  <MultiLingualText size="300" color="grey.900">
                    Terminations
                  </MultiLingualText>
                </ListSelectionStyled>
              </Box>
            </Box>
            {values.commodityType === MetadataTableName.CABLE_TERM && (
              <Box flexDirection={{ sm: 'column' }} mt={{ sm: '550' }}>
                <SectionHeaderStyled>
                  <MultiLingualText size="400" weight="600" color="grey.800">
                    Scope
                  </MultiLingualText>
                </SectionHeaderStyled>
                <Box flexDirection={{ sm: 'column' }}>
                  <ListSelectionStyled
                    disabled={!canUpdatePrimaryStatus}
                    onClick={() => {
                      if (!canUpdatePrimaryStatus) return;
                      setFieldValue('cableTermType', CableTermType.F);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio size={TextFieldSize.MEDIUM} checked={values.cableTermType === CableTermType.F} readOnly />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      FROM Terminations
                    </MultiLingualText>
                  </ListSelectionStyled>
                  <ListSelectionStyled
                    disabled={!canUpdatePrimaryStatus}
                    onClick={() => {
                      if (!canUpdatePrimaryStatus) return;
                      setFieldValue('cableTermType', CableTermType.T);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio size={TextFieldSize.MEDIUM} checked={values.cableTermType === CableTermType.T} readOnly />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      TO Terminations
                    </MultiLingualText>
                  </ListSelectionStyled>
                  <ListSelectionStyled
                    disabled={!canUpdatePrimaryStatus}
                    onClick={() => {
                      if (!canUpdatePrimaryStatus) return;
                      setFieldValue('cableTermType', CableTermType.B);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio size={TextFieldSize.MEDIUM} checked={values.cableTermType === CableTermType.B} readOnly />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Both FROM and TO ends
                    </MultiLingualText>
                  </ListSelectionStyled>
                </Box>
              </Box>
            )}
            <Box mb={{ sm: '450' }} mt={{ sm: '450' }}>
              <FormField
                alwaysEditable={canUpdatePrimaryStatus}
                memberName="oldPrimaryStatus"
                showPicker={false}
                size={FormLabelSize.MEDIUM}
                customMetadataColumn={oldPrimaryStatusMetadata}
              >
                <SelectFieldComponent
                  items={getPrimaryStatusValues('oldPrimaryStatus')}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('oldPrimaryStatus', changes.selectedItem.value);
                  }}
                  selectedItem={selectedItem(getPrimaryStatusValues('oldPrimaryStatus'), values.oldPrimaryStatus)}
                />
              </FormField>
            </Box>
            <Box mb={{ sm: '450' }}>
              <FormField
                alwaysEditable={canUpdatePrimaryStatus}
                memberName="newPrimaryStatus"
                showPicker={false}
                size={FormLabelSize.MEDIUM}
                customMetadataColumn={newPrimaryStatusMetadata}
              >
                <SelectFieldComponent
                  items={getPrimaryStatusValues('newPrimaryStatus')}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('newPrimaryStatus', changes.selectedItem.value);
                    if (changes.selectedItem.value === PrimaryStatus.C && !canOverridePriStatJtoR)
                      setFieldValue('newConstructionStatus', ConstructionStatus.PART_INSTALLED);
                    else setFieldValue('newConstructionStatus', null);
                  }}
                  selectedItem={selectedItem(getPrimaryStatusValues('newPrimaryStatus'), values.newPrimaryStatus)}
                />
              </FormField>
            </Box>
            <Box mb={{ sm: '550' }}>
              <FormField
                alwaysEditable={
                  values.newPrimaryStatus === PrimaryStatus.C && canUpdatePrimaryStatus && !canOverridePriStatJtoR
                }
                memberName="newConstructionStatus"
                showPicker={false}
                customMetadataColumn={newConstructionStatusMetadata}
                size={FormLabelSize.MEDIUM}
              >
                <SelectFieldComponent
                  items={values.newPrimaryStatus === PrimaryStatus.C ? constructionStatusValues.slice(1) : []}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('newConstructionStatus', changes.selectedItem.value);
                  }}
                  selectedItem={selectedItem(constructionStatusValues, values.newConstructionStatus)}
                />
              </FormField>
            </Box>
            <ListSelectionStyled
              disabled={
                !(
                  includes([PrimaryStatus.J, PrimaryStatus.C], values.oldPrimaryStatus) &&
                  values.commodityType === CommodityName.CABLE &&
                  canUpdatePrimaryStatus
                )
              }
              onClick={() => {
                if (
                  !(
                    includes([PrimaryStatus.J, PrimaryStatus.C], values.oldPrimaryStatus) &&
                    values.commodityType === CommodityName.CABLE &&
                    canUpdatePrimaryStatus
                  )
                )
                  return;
                setFieldValue('retainReels', !values.retainReels);
              }}
            >
              <Box mr={{ sm: '400' }}>
                <Checkbox
                  checked={values.retainReels}
                  size={TextFieldSize.MEDIUM}
                  disabled={
                    !(
                      includes([PrimaryStatus.J, PrimaryStatus.C], values.oldPrimaryStatus) &&
                      values.commodityType === CommodityName.CABLE &&
                      canUpdatePrimaryStatus
                    )
                  }
                  readOnly
                />
              </Box>
              <Box>
                <MultiLingualText size="300" color="grey.800">
                  Retain Cable Reel Cut Lengths
                </MultiLingualText>
              </Box>
            </ListSelectionStyled>
            <Box>
              <Filter
                entityName={
                  values.commodityType === MetadataTableName.CABLE_TERM
                    ? toUpper(CommodityName.CABLE)
                    : toUpper(values.commodityType)
                }
                value={values.whereClause}
                handleFilter={(value) => setFieldValue('whereClause', value)}
                disabled={!canUpdatePrimaryStatus}
              />
            </Box>
            <Box mt={{ sm: '500' }} mb={{ sm: '450' }}>
              <MultiLingualText color="red.500" weight="700">
                NOTE: Overriding a primary status will REMOVE pending revised cards.
              </MultiLingualText>
            </Box>
          </ContentStyled>
        </FormContainerStyled>
      </FormStyled>
      <CommodityFooter
        saveHandler={saveHandler}
        loading={loading}
        disableSave={loading || isEmpty(values.whereClause) || !canUpdatePrimaryStatus}
        disableCancel={!dirty || !touched || loading}
        cancelHandler={() => handleReset()}
      />
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
    </Box>
  );
};

export default OverridePrimaryStatusForm;
